import { createSelector } from '@reduxjs/toolkit';
import {
  intervalMinMaxShrSelector,
  millisecondsIntervalSelector,
  shrArraySelector,
} from '../../wave/selectors/wave.selectors';
import {
  ASYSTOLE_RR_THRESHOLD,
  isVentricularRhythm,
  isVentricularTachycardia,
  MODERATE_BRADYCARDIA_HR_THRESHOLD,
  MODERATE_TACHYCARDIA_HR_THRESHOLD,
  PAUSE_RR_THRESHOLD,
  PRIMARY_LABELS_CONFIG,
  SEVERE_BRADYCARDIA_HR_THRESHOLD,
  SEVERE_TACHYCARDIA_HR_THRESHOLD,
} from '../utils/primary-labels.util';
import { primaryDefinitionsSelector } from './diagnostic.selectors';

const pauseAsystoleSelector = createSelector(
  millisecondsIntervalSelector,
  (intervals) =>
    intervals.some((rr) => rr.duration >= ASYSTOLE_RR_THRESHOLD)
      ? 'CL_rh_6'
      : intervals.some((rr) => rr.duration >= PAUSE_RR_THRESHOLD)
      ? 'CL_rh_5'
      : null,
);

const tachycardiaSelector = createSelector(
  intervalMinMaxShrSelector,
  ([_, maxShr]) =>
    maxShr != null && maxShr?.shr >= SEVERE_TACHYCARDIA_HR_THRESHOLD
      ? 'CL_rh_10'
      : maxShr != null && maxShr?.shr >= MODERATE_TACHYCARDIA_HR_THRESHOLD
      ? 'CL_rh_9'
      : null,
);

const bradycardiaSelector = createSelector(
  intervalMinMaxShrSelector,
  ([minShr, _]) =>
    minShr != null && minShr?.shr <= SEVERE_BRADYCARDIA_HR_THRESHOLD
      ? 'CL_rh_8'
      : minShr != null && minShr?.shr <= MODERATE_BRADYCARDIA_HR_THRESHOLD
      ? 'CL_rh_7'
      : null,
);

const ventricularRunSelector = createSelector(shrArraySelector, (shrArray) =>
  isVentricularTachycardia(shrArray)
    ? 'CL_rh_v_17'
    : isVentricularRhythm(shrArray)
    ? 'CL_rh_v_18'
    : null,
);

export const primaryLabelsSelector = createSelector(
  pauseAsystoleSelector,
  bradycardiaSelector,
  tachycardiaSelector,
  ventricularRunSelector,
  primaryDefinitionsSelector,
  (
    pauseAsystole,
    bradycardia,
    tachycardia,
    ventricularRun,
    primaryDefinitions,
  ) => {
    const validLabels = [
      pauseAsystole,
      bradycardia,
      tachycardia,
      ventricularRun,
    ].filter((run) => run != null);
    return primaryDefinitions
      .filter(({ code }) => validLabels.includes(code))
      .map(({ code }) => PRIMARY_LABELS_CONFIG[code]);
  },
);
